<template>
  <div class="d-flex flex-column align-center px-6 py-8">
    <PayabliLogo aria-label="Payabli logo" class="mb-4" />
    <p class="text-body-2 text-grey-darken-1 text-center mb-6 max-w-xl">
      Send client settlement disbursements through Payabli.
    </p>

    <div v-if="payabliState.loading" class="max-w-xl w-full">
      <v-skeleton-loader type="list-item-two-line, divider, list-item"></v-skeleton-loader>
    </div>

    <div
      v-else-if="!features.paayabli"
      class="d-flex align-center pa-5 rounded-lg bg-grey-lighten-4 max-w-xl w-full shadow-sm"
    >
      <v-avatar color="blue-grey-darken-1" size="44" class="mr-4 flex-shrink-0">
        <LlIcon icon="mdi-information-outline" color="white" size="22" />
      </v-avatar>
      <div class="text-body-2 text-grey-darken-2">
        Payabli e-disbursements is not available for your account. Please
        contact LeanLaw support for more information.
      </div>
    </div>

    <div v-else class="max-w-xl w-full" data-cy="payabli-status-card">
      <div
        v-if="!payabliState.status.connected"
        class="d-flex align-center pa-5 rounded-lg bg-grey-lighten-4 shadow-sm"
        data-cy="payabli-not-connected"
      >
        <v-avatar color="blue-grey-darken-1" size="44" class="mr-4 flex-shrink-0">
          <LlIcon icon="mdi-close" color="white" size="22" />
        </v-avatar>
        <div>
          <div class="text-subtitle-1 font-weight-bold text-grey-darken-3">
            Not Connected
          </div>
          <div class="text-body-2 text-grey-darken-2 mt-1">
            Contact LeanLaw support to enable e-disbursements.
          </div>
        </div>
      </div>

      <div v-else data-cy="payabli-connected">
        <div class="d-flex align-center pa-5 rounded-lg bg-green-lighten-5 shadow-sm">
          <v-avatar color="success" size="44" class="mr-4 flex-shrink-0">
            <LlIcon icon="mdi-check" color="white" size="22" />
          </v-avatar>
          <div>
            <div class="d-flex align-center flex-wrap">
              <span class="text-subtitle-1 font-weight-bold text-grey-darken-3 mr-2">
                Connected
              </span>
              <ll-status-badge>as of {{ formattedConnectedSince }}</ll-status-badge>
              <ll-status-badge
                v-if="payabliState.status.sandbox"
                tone="amber"
                class="ml-2"
                data-cy="payabli-sandbox-badge"
              >
                Sandbox
              </ll-status-badge>
            </div>
            <div class="text-body-2 text-grey-darken-2 mt-1">
              {{ payabliState.status.defaultPaypointLabel }}
            </div>
          </div>
        </div>
        <div
          v-if="payabliState.status.paypointCount > 1"
          class="d-flex align-center text-body-2 text-grey-darken-1 mt-3 ml-1"
        >
          <LlIcon icon="mdi-bank-outline" size="16" color="grey-darken-1" class="mr-1" />
          {{ payabliState.status.paypointCount - 1 }} other
          {{ pluralize(payabliState.status.paypointCount - 1, 'paypoint') }} connected
        </div>

        <v-divider class="my-6"></v-divider>

        <div class="d-flex align-center mb-2">
          <span class="text-body-2 font-weight-medium">Processing-fee category</span>
          <LlTooltip icon="help" class="ml-2">
            The QuickBooks expense account Payabli's processing fee is posted to.
          </LlTooltip>
        </div>
        <ll-select
          data-cy="payabli-fee-account"
          v-model="payabliState.feeExpenseAccountId"
          :items="payabliState.expenseAccounts"
          item-title="label"
          item-value="id"
          :return-object="false"
          :is-autocomplete="true"
          :hide-details="true"
          :loading="payabliState.saving"
          :disabled="payabliState.saving"
          placeholder="Payabli Processing Fees"
          class="max-w-sm"
          @update:modelValue="onFeeAccountChange"
        />
        <div class="text-caption text-grey-darken-1 mt-1">
          Default — change to post fees to a different QuickBooks expense category.
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { reactive, computed, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import { useMeStore } from '@/services/UserService/useMeStore';
import { usePayabliIntegrationStore, PayabliStatus } from './stores/usePayabliIntegrationStore';
import { useSnackbarHandler } from '@/composables/useSnackbarHandler';
import LlSelect from '@/components/LlSelect.vue';
import LlTooltip from '@/components/LlTooltip.vue';
import LlStatusBadge from '@/components/LlStatusBadge.vue';
import PayabliLogo from '@/components/SVG/PayabliLogo.vue';
import { pluralize, getShortDateString } from '@/services/utils';
import LlIcon from '@/components/LlIcon.vue';

interface ExpenseAccount {
  id: string;
  label: string;
}

interface AccountingService {
  accounts: () => Promise<Record<string, unknown[]>>;
  combineQuickBooksAccounts: (
    accounts: Record<string, unknown[]>,
    types: string[],
  ) => ExpenseAccount[];
}

const props = defineProps<{
  accountingService: AccountingService;
}>();

const { handleError, handleSuccess } = useSnackbarHandler();
const meStore = useMeStore();
const { features, company } = storeToRefs(meStore);
const payabliStore = usePayabliIntegrationStore();

const payabliState = reactive({
  loading: true,
  saving: false,
  status: payabliStore.status as PayabliStatus,
  expenseAccounts: [] as ExpenseAccount[],
  feeExpenseAccountId: null as string | null,
});

const formattedConnectedSince = computed(() => {
  if (!payabliState.status.connectedSince) return '';
  return getShortDateString(payabliState.status.connectedSince);
});

onMounted(async () => {
  payabliState.loading = true;
  try {
    await meStore.getMe();

    if (features.value?.paayabli) {
      payabliState.status = await payabliStore.fetchStatus(company.value.id);

      if (payabliState.status.connected) {
        payabliState.feeExpenseAccountId = payabliState.status.feeExpenseAccountId;
        const accounts = await props.accountingService.accounts();
        payabliState.expenseAccounts = props.accountingService.combineQuickBooksAccounts(
          accounts,
          ['expense'],
        );
      }
    }
  } catch (error) {
    handleError('Failed to load Payabli settings');
  } finally {
    payabliState.loading = false;
  }
});

async function onFeeAccountChange(feeExpenseAccountId: string) {
  payabliState.saving = true;
  try {
    payabliState.status = await payabliStore.updateFeeAccount(
      company.value.id,
      feeExpenseAccountId,
    );
    handleSuccess('Payabli fee category updated');
  } catch (error) {
    payabliState.feeExpenseAccountId = payabliState.status.feeExpenseAccountId;
  } finally {
    payabliState.saving = false;
  }
}
</script>
